import { ServiceExtraForm } from '@components';
import { useView } from '@hooks';
import { Utils } from '@utils';
import { Input, InputNumber, Tag, Typography } from 'antd';
import React from 'react';
import { IServiceDetailsPresentation } from './ServiceDetailsCard.props';
import { RemoveServiceButton } from './RemoveServiceButton';

const { Text } = Typography;

export const ServiceDetailsPresentation: React.FC<IServiceDetailsPresentation> = ({
  service,
  disabled,
  showDiscount,
  showTotals,
  subTotal,
  vatAmount,
  parts,
  onRemovePart,
  onRemove,
  canRemove,
  onUpdatePart,
  onAddPartToPurchaseOrder,
  handlePriceUpdate,
  handleDurationUpdate,
  handleDiscountUpdate,
  handleNoteUpdate,
}) => {
  const { isSmallDesktop } = useView();

  const lbl: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: 'rgba(0, 0, 0, 0.85)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
    display: 'block',
  };

  // Shared grid: Name | Type | Cost | Margin | Sale/Price | Qty/Dur | Disc | Sub | VAT | Total | Del
  const allCols = isSmallDesktop
    ? '1fr 110px 60px 55px 70px 60px 60px 72px 72px 86px 28px'
    : '1fr 130px 70px 65px 80px 70px 70px 90px 90px 100px 32px';
  // Discount is the 7th column and Sub, VAT and Total the 8th to 10th; the extras' grid below drops
  // the same ones, so what is left stays aligned.
  const gridCols = allCols
    .split(' ')
    .filter((_, i) => (showDiscount || i !== 6) && (showTotals || i < 7 || i > 9))
    .join(' ');
  const gridGap = isSmallDesktop ? 6 : 8;

  return (
    <>
      {/* ── Service row (grid) ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: gridGap,
          alignItems: 'end',
          padding: '6px 10px',
          marginBottom: 4,
        }}
      >
        {/* Name + tag — span first 4 cols */}
        <div
          style={{
            gridColumn: '1 / 5',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            alignSelf: 'end',
            paddingBottom: 4,
          }}
        >
          <Text style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
            {service.name}
          </Text>
          <Tag
            color={service.isFixed ? 'blue' : 'green'}
            style={{ margin: 0, fontSize: 13, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}
          >
            {service.isFixed ? 'Fixed' : 'Labour'}
          </Tag>
        </div>

        {/* Price — col 5 aligns with Sale */}
        <div>
          <Text style={lbl}>{service.isFixed ? 'Price (£)' : '£/hour'}</Text>
          <InputNumber
            min={0}
            step={1}
            precision={2}
            style={{ width: '100%' }}
            value={service.price}
            disabled={disabled}
            onChange={(v: number | null) =>
              handlePriceUpdate(service.id, v !== null ? Number(Utils.formatNumber(v, 2)) : 0)
            }
          />
        </div>

        {/* Duration — col 6 aligns with Qty */}
        <div>
          <Text style={lbl}>Duration</Text>
          <InputNumber
            min={0}
            step={0.1}
            precision={1}
            style={{ width: '100%' }}
            disabled={disabled}
            value={service.duration}
            onChange={(v: number | null) =>
              handleDurationUpdate(service.id, v !== null ? Number(Utils.formatNumber(v, 2)) : 0)
            }
          />
        </div>

        {/* Discount — col 7 aligns with Disc */}
        {showDiscount && (
          <div>
            <Text style={lbl}>Disc. %</Text>
            <InputNumber
              min={0}
              max={100}
              step={1}
              precision={0}
              style={{ width: '100%' }}
              value={service.discount}
              disabled={disabled}
              onChange={(v: number | null) =>
                handleDiscountUpdate(service.id, v !== null ? Number(Utils.formatNumber(v, 2)) : 0)
              }
            />
          </div>
        )}

        {showTotals && (
          <>
            {/* Sub total */}
            <div style={{ paddingBottom: 4, textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>SUB TOTAL</Text>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                {Utils.formatCurrency(subTotal)}
              </span>
            </div>

            {/* VAT */}
            <div style={{ paddingBottom: 4, textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>VAT</Text>
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                {Utils.formatCurrency(vatAmount)}
              </span>
            </div>

            {/* Total */}
            <div style={{ paddingBottom: 4, textAlign: 'right' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block' }}>TOTAL</Text>
              <Tag
                color="blue"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  padding: '2px 8px',
                  margin: 0,
                  minWidth: 70,
                  textAlign: 'center',
                  display: 'inline-block',
                }}
              >
                {Utils.formatCurrency(Utils.roundToTwoDecimals(subTotal + vatAmount))}
              </Tag>
            </div>
          </>
        )}

        {/* Remove the service - same column as the parts' bins below. A saved service asks first;
            one added this session just goes, there is nothing to lose. */}
        <div style={{ paddingBottom: 4, textAlign: 'right' }}>
          {onRemove && (
            <RemoveServiceButton
              disabled={disabled || canRemove === false}
              onRemove={onRemove}
            />
          )}
        </div>
      </div>

      {/* ── Note input ── */}
      <div style={{ marginBottom: 8, padding: '0 10px' }}>
        <Text style={lbl}>Note</Text>
        <Input.TextArea
          placeholder="Note..."
          value={service.note || ''}
          disabled={disabled}
          autoSize={{ minRows: 1 }}
          onChange={(e) => handleNoteUpdate(service.id, e.target.value)}
          style={{ fontSize: 12 }}
        />
      </div>

      {/* ── Parts & extras ── */}
      {parts.length > 0 && (
        <div>
          {parts.map((servicePart, index) => (
            <ServiceExtraForm
              key={`${service.id}part${index}`}
              index={index}
              extra={servicePart}
              onRemove={() => onRemovePart(index)}
              onUpdate={(values) => onUpdatePart(index, values)}
              disabled={disabled}
              onAddToPurchaseOrder={onAddPartToPurchaseOrder ? () => onAddPartToPurchaseOrder(index) : undefined}
            />
          ))}
        </div>
      )}
    </>
  );
};
